import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, ConfigProvider } from 'antd';
import { useSearchParams } from 'react-router-dom';
import Tabs from '../../../components/Tabs';
// 결제 관리 화면과 같은 화면 톤(설정 화면 공통 스타일)을 쓴다
import { SettingsWorkspaceStyle } from '../../pgProviderRoute/common/styles';
import { FeatureFlagEnvironment } from '../../../lib/api/featureFlag';
import { featureFlagQuery } from '../queries';
import { environmentLabel, isCautionEnvironment } from '../utils/featureFlagLabels';
import { FeatureFlagPanel } from './FeatureFlagPanel';
import { FeatureFlagHistoryPanel } from './FeatureFlagHistoryPanel';
import { EnvironmentNoticeStyle, EnvironmentTag } from './styles';

type FeatureFlagWorkspaceProps = { canUpdate: boolean };
const TAB_KEYS = ['flags', 'history'] as const;

/** 어느 환경을 바꾸는지 맨 위에서 보이게 — prod 는 빨간색 */
const EnvironmentNotice = (props: { environment: FeatureFlagEnvironment | string }): React.JSX.Element => {
  const { environment } = props;
  const tag = (
    <EnvironmentTag $production={isCautionEnvironment(environment)}>{environmentLabel(environment)}</EnvironmentTag>
  );
  if (environment === 'production')
    return (
      <Alert
        className="settings-inline-alert"
        type="error"
        showIcon
        message={
          <span className="flag-env-message">
            {tag}
            운영(prod) 환경입니다 — 바꾸면 고객에게 바로 반영됩니다.
          </span>
        }
        description="prod 는 QA 플래그의 기본값이 OFF 입니다. 여기서 켜는 것이 출시이고, 출시 플래그를 끄는 것이 긴급 OFF 입니다."
      />
    );
  if (environment === 'local')
    return (
      <Alert
        className="settings-inline-alert"
        type="warning"
        showIcon
        message={
          <span className="flag-env-message">
            {tag}
            로컬 환경에서는 바꿀 수 없습니다.
          </span>
        }
        description="로컬 판정은 DB 값을 보지 않습니다(QA · 출시 항상 ON). 로컬에서 켜고 끄기는 local-flag-data 로 합니다."
      />
    );
  if (environment !== 'development' && environment !== 'test')
    return (
      <Alert
        className="settings-inline-alert"
        type="warning"
        showIcon
        message={
          <span className="flag-env-message">
            {tag}
            어느 환경인지 알 수 없습니다 — 운영일 수 있으니 바꾸기 전에 확인하세요.
          </span>
        }
        description="서버가 알려 준 환경 값이 local · development · production 이 아닙니다. 값은 이 admin 이 붙은 환경 DB 에 저장됩니다."
      />
    );
  return (
    <Alert
      className="settings-inline-alert"
      type="info"
      showIcon
      message={
        <span className="flag-env-message">{tag}이 환경의 값입니다. prod 는 prod admin 에서 따로 바꿉니다.</span>
      }
      description="저장하면 재시작 없이 몇 초 안에 이 환경의 서버 전체에 반영됩니다. 플래그 목록 · 상태값은 코드(JIRA_TICKETS)에서 오고, 여기서는 켜짐 · 꺼짐만 덮어씁니다."
    />
  );
};

export const FeatureFlagWorkspace = (props: FeatureFlagWorkspaceProps): React.JSX.Element => {
  const { canUpdate } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'history' ? 'history' : 'flags';
  const { data } = useQuery(featureFlagQuery.list());

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#7950f2',
          borderRadius: 4,
          colorText: '#283141',
          colorTextSecondary: '#626e7f',
          controlHeight: 36,
          controlHeightSM: 32,
        },
        components: { Button: { fontWeight: 500, paddingInline: 16, paddingInlineSM: 12 } },
      }}
    >
      <App>
        <SettingsWorkspaceStyle>
          {data && (
            <EnvironmentNoticeStyle>
              <EnvironmentNotice environment={data.environment} />
            </EnvironmentNoticeStyle>
          )}
          {!canUpdate && (
            <Alert
              className="settings-inline-alert"
              type="info"
              showIcon
              message="조회 권한으로 보고 있습니다. 값을 바꾸려면 「서버 피처 플래그 · 켜기 · 끄기」 권한이 필요합니다."
            />
          )}
          <Tabs
            activeKey={TAB_KEYS.indexOf(activeTab)}
            onChange={(key: number) =>
              setSearchParams(
                (previous) => {
                  const next = new URLSearchParams(previous);
                  next.set('tab', TAB_KEYS[key] ?? 'flags');
                  return next;
                },
                { replace: true },
              )
            }
            destroyInactiveTabPane={false}
            list={[
              { id: 0, title: '플래그', forceRender: true, content: <FeatureFlagPanel canUpdate={canUpdate} /> },
              {
                id: 1,
                title: '변경 이력',
                content: activeTab === 'history' ? <FeatureFlagHistoryPanel /> : null,
              },
            ]}
          />
        </SettingsWorkspaceStyle>
      </App>
    </ConfigProvider>
  );
};
